// S-09 - Resource Import (Admin) - US-09
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { userApi, ApiRequestError, type ImportBulkReport } from "../api/api";

export function ResourceImportPage() {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<ImportBulkReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function downloadTemplate() {
    setError(null);
    try {
      const blob = await userApi.downloadTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "skillmap-users-template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t("download_failed"));
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!file) {
      setError(t("choose_xlsx"));
      return;
    }
    setBusy(true);
    setError(null);
    setReport(null);
    try {
      const result = await userApi.importBulk(file);
      setReport(result);
    } catch (err) {
      setError(
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("import_failed")
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sm-page">
      <div className="sm-page-header">
        <div>
          <h1>{t("import_users_title")}</h1>
          <p>{t("import_users_help")}</p>
        </div>
        <div className="sm-toolbar">
          <Link to="/users" className="sm-btn sm-btn-ghost">
            {t("back_users")}
          </Link>
        </div>
      </div>

      <div className="sm-form-card">
        <button
          type="button"
          className="sm-btn sm-btn-secondary"
          style={{ marginBottom: 16 }}
          onClick={() => void downloadTemplate()}
        >
          ⬇ {t("download_excel_template")}
        </button>

        <form onSubmit={onSubmit}>
          <div className="sm-form-actions" style={{ flexDirection: "column", alignItems: "flex-start" }}>
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <button type="submit" className="sm-btn sm-btn-primary" disabled={busy}>
              {busy ? t("import_in_progress") : t("import")}
            </button>
          </div>
        </form>
      </div>

      {error ? (
        <div className="sm-alert sm-alert-error" role="alert">
          {error}
        </div>
      ) : null}

      {report ? (
        <div className="sm-section">
          <h2>{t("report")}</h2>
          <p>
            {t("created_n")} : <strong>{report.created}</strong> — {t("updated_n")} :{" "}
            <strong>{report.updated}</strong> — {t("errors_n")} : <strong>{report.errors.length}</strong>
          </p>
          {report.errors.length > 0 ? (
            <ul>
              {report.errors.map((e) => (
                <li key={`${e.row}-${e.message}`}>
                  {t("row_n", { row: e.row })} {e.message}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
